import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMeeting } from '../contexts/MeetingContext';
import { Calendar, Clock, User, CheckCircle, XCircle, Sparkles, Moon, Sun, AlertCircle, ArrowRight } from 'lucide-react';

type MeetingStatus = 'ongoing' | 'ended' | 'upcoming' | null;

const AcceptInvite: React.FC = () => {
  const { token } = useParams();
  const { user, login } = useAuth();
  const { acceptInvite, jitsiTokenFromInvite } = useMeeting();
  const navigate = useNavigate();

  const [meeting, setMeeting] = useState<any>(null);
  const [inviteJwtToken, setInviteJwtToken] = useState<string>('');
  const [meetingStatus, setMeetingStatus] = useState<MeetingStatus>(null);
  const [showGuestOption, setShowGuestOption] = useState(false);
  const [guestEmail, setGuestEmail] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    if (!token) {
      console.log('Token not available yet');
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const jwtToken = params.get('token');

    if (jwtToken) {
      setInviteJwtToken(jwtToken);
      setTimeout(() => {
        attemptAutoLogin(jwtToken);
      }, 100);
    } else {
      fetchInviteDetails();
    }
  }, [token]);

  const checkMeetingStatus = (meetingData: any): MeetingStatus => {
    if (!meetingData.meeting_date || !meetingData.start_time || !meetingData.end_time) {
      return 'upcoming';
    }

    const now = new Date();
    const meetingDate = new Date(meetingData.meeting_date);

    const [startHour, startMin] = meetingData.start_time.split(':').map(Number);
    const [endHour, endMin] = meetingData.end_time.split(':').map(Number);

    // Use UTC to match server-side storage and avoid timezone mismatches
    const meetingStart = new Date(Date.UTC(
      meetingDate.getUTCFullYear(), meetingDate.getUTCMonth(), meetingDate.getUTCDate(),
      startHour, startMin, 0
    ));
    const meetingEnd = new Date(Date.UTC(
      meetingDate.getUTCFullYear(), meetingDate.getUTCMonth(), meetingDate.getUTCDate(),
      endHour, endMin, 0
    ));

    const nowMs = now.getTime();

    if (nowMs >= meetingStart.getTime() && nowMs <= meetingEnd.getTime()) {
      return 'ongoing';
    } else if (nowMs > meetingEnd.getTime()) {
      // Instant meetings should remain "ongoing" past auto-generated end time
      if (meetingData.meeting_type === 'instant') {
        return 'ongoing';
      }
      return 'ended';
    } else {
      return 'upcoming';
    }
  };

  const attemptAutoLogin = async (jwtToken: string) => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/meetings/auto-login-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ inviteToken: jwtToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.requiresSignup) {
          setError(`Please sign up with ${data.email} to accept this invitation.`);
          setShowGuestOption(true);
          setGuestEmail(data.email);
        } else if (data.requiresVerification) {
          setError('Please verify your email first.');
        } else if (data.expired) {
          setError('This invitation link has expired. Please request a new one.');
        } else {
          setError(data.message || 'Auto-login failed');
        }
        fetchInviteDetails();
        return;
      }

      await fetchInviteDetailsAndCheckStatus(data.meetingId, jwtToken);
      await acceptInvite(token!, inviteJwtToken);

    } catch (err: any) {
      console.error('Auto-login error:', err);
      fetchInviteDetails();
    } finally {
      setLoading(false);
    }
  };

  const fetchInviteDetailsAndCheckStatus = async (meetingId?: number, jwtToken?: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/meetings/invite/${token}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || 'Failed to fetch invite details');
      }

      if (!data.meeting) {
        throw new Error('No meeting data received');
      }

      setMeeting(data.meeting);
      const status = checkMeetingStatus(data.meeting);
      setMeetingStatus(status);

      if (status === 'ongoing') {
        const tokenToUse = jwtToken || inviteJwtToken;

        if (tokenToUse) {
          const jitsiToken = await jitsiTokenFromInvite(tokenToUse);
          window.location.href = `${data.meeting.meeting_link}?jwt=${jitsiToken}`;
        }
      }

    } catch (err: any) {
      console.error('Error fetching invite details:', err);
      setError(err.message);
    }
  };

  const fetchInviteDetails = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/meetings/invite/${token}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || 'Failed to fetch invite details');
      }

      if (!data.meeting) {
        throw new Error('No meeting data received');
      }

      setMeeting(data.meeting);
      const status = checkMeetingStatus(data.meeting);
      setMeetingStatus(status);

      if (data.participant?.accepted) {
        fetchInviteDetailsAndCheckStatus(data.meeting.id, inviteJwtToken);
      }
    } catch (err: any) {
      console.error('Error fetching invite details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinAsGuest = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!inviteJwtToken) {
        setError('No invitation token available');
        return;
      }

      await fetchInviteDetailsAndCheckStatus(meeting?.id, inviteJwtToken);

    } catch (err: any) {
      console.error('Join as guest error:', err);
      setError('Failed to join as guest');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    try {
      setLoading(true);

      if (!user) {
        sessionStorage.setItem('returnUrl', window.location.pathname);
        return;
      }

      await acceptInvite(token!);
      navigate('/');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    try {
      if (!user) {
        sessionStorage.setItem('pendingInvite', token!);
        return;
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const formatTimeTo12Hour = (timeString: string) => {
    if (!timeString) return 'N/A';

    if (timeString.includes('AM') || timeString.includes('PM')) {
      return timeString.toUpperCase();
    }

    const [hours, minutes] = timeString.split(":");
    const date = new Date();
    date.setHours(Number(hours), Number(minutes));
    return date
      .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true })
      .replace(/am|pm/i, (match) => match.toUpperCase());
  };

  // Render status banner based on current state
  const renderStatusBanner = () => {
    if (error) {
      return (
        <div className={`mb-3 p-3 rounded-xl border ${darkMode ? 'bg-red-500/10 border-red-500/20' : 'bg-red-50 border-red-200'}`}>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-500" />
            </div>
            <div className="flex-1">
              {/* <h4 className={`font-semibold mb-1 ${darkMode ? 'text-red-300' : 'text-red-800'}`}>
                Oops! 
              </h4> */}
              <p className={`text-sm ${darkMode ? 'text-red-300/80' : 'text-red-700'}`}>
                {error}
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (meetingStatus === 'ended') {
      return (
        <div className={`mb-4 p-4 rounded-xl border ${darkMode ? 'bg-orange-500/10 border-orange-500/20' : 'bg-orange-50 border-orange-200'}`}>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-orange-500" />
            </div>
            <div className="flex-1">
              <h4 className={`font-semibold mb-1 ${darkMode ? 'text-orange-300' : 'text-orange-800'}`}>
                Meeting Ended
              </h4>
              <p className={`text-sm ${darkMode ? 'text-orange-300/80' : 'text-orange-700'}`}>
                This meeting has already concluded.
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (meetingStatus === 'upcoming') {
      return (
        <div className={`mb-4 p-4 rounded-xl border ${darkMode ? 'bg-blue-500/10 border-blue-500/20' : 'bg-blue-50 border-blue-200'}`}>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-500" />
            </div>
            <div className="flex-1">
              <h4 className={`font-semibold mb-1 ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                Meeting Not Started Yet
              </h4>
              <p className={`text-sm ${darkMode ? 'text-blue-300/80' : 'text-blue-700'}`}>
                The meeting hasn't started. Please check back at the scheduled time.
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (meetingStatus === 'ongoing') {
      return (
        <div className={`mb-4 p-4 rounded-xl border ${darkMode ? 'bg-green-500/10 border-green-500/20' : 'bg-green-50 border-green-200'}`}>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
            <div className="flex-1">
              <h4 className={`font-semibold mb-1 ${darkMode ? 'text-green-300' : 'text-green-800'}`}>
                Meeting in Progress
              </h4>
              <p className={`text-sm ${darkMode ? 'text-green-300/80' : 'text-green-700'}`}>
                The meeting is currently active. Join now!
              </p>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  // Render appropriate action buttons based on state
  const renderActionButtons = () => {
    // Guest option when there's an error and guest option is available
    if (error && showGuestOption) {
      return (
        <div className="space-y-2 justify-center">
          <div className={`p-2 rounded-xl ${darkMode ? 'bg-pink-500/10 border border-pink-500/20' : 'bg-pink-50 border border-pink-200'}`}>
            <p className={`text-sm mb-2 ${darkMode ? 'text-pink-300' : 'text-pink-800'}`}>
              Don't have an account? You can still join as a guest!
            </p>
          </div>

          <button
            onClick={handleJoinAsGuest}
            disabled={loading}
            className="w-full bg-gradient-to-r from-pink-500 to-red-500 text-white font-semibold py-3.5 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-5 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Joining...
              </>
            ) : (
              <>
                <User className="w-5 h-5" />
                Join as Guest
              </>
            )}
          </button>

          <button
            onClick={() => navigate('/')}
            className={`w-full font-semibold py-3 px-4 rounded-xl border-2 transition-all duration-200 ${darkMode
                ? 'border-white/20 text-white hover:bg-white/5'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
          >
            Back to Dashboard
          </button>
        </div>
      );
    }

    // Meeting ended or has error without guest option
    if (meetingStatus === 'ended' || (error && !showGuestOption)) {
      return (
        <button
          onClick={() => navigate('/')}
          className="w-full bg-gradient-to-r from-pink-500 to-red-500 text-white font-semibold py-3.5 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 flex items-center justify-center gap-2"
        >
          <ArrowRight className="w-5 h-5" />
          Go to Dashboard
        </button>
      );
    }

    // Meeting upcoming - show dashboard button
    if (meetingStatus === 'upcoming') {
      return (
        <>
          <div className="space-x-5 mb-4 flex flex-row items-center justify-center gap-4">
            <button
              onClick={handleAccept}
              disabled={loading}
              className=" bg-gradient-to-r from-pink-500/80 to-red-500/80 text-white font-semibold py-3.5 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Accept Invitation
                </>
              )}
            </button>

            <button
              onClick={handleDecline}
              disabled={loading}
              className={` font-semibold py-3.5 px-6 rounded-xl border-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${darkMode
                  ? 'border-white/20 text-white hover:bg-white/5'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
            >
              <XCircle className="w-5 h-5" />
              Decline
            </button>
          </div>
          <button
            onClick={() => navigate('/')}
            className="w-full bg-gradient-to-r from-pink-500 to-red-500 text-white font-semibold py-3.5 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 flex items-center justify-center gap-2"
          >
            <ArrowRight className="w-5 h-5" />
            Go to Dashboard
          </button>
        </>
      );
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center min-h-screen ${darkMode ? 'bg-gradient-to-br from-[#181824] to-[#232336]' : 'bg-gradient-to-br from-gray-50 to-blue-50'}`}>
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 border-4 border-pink-500 border-t-transparent rounded-full animate-spin"></div>
          <p className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-600'}`}>Loading meeting details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-screen w-full flex overflow-hidden ${darkMode ? 'bg-gradient-to-br from-[#181824] to-[#232336]' : 'bg-gradient-to-br from-gray-50 to-blue-50'}`}>
      {/* Left: Hero Section (60%) */}
      <div className="hidden lg:flex flex-1 items-center justify-center relative overflow-hidden" style={{ flexBasis: '60%' }}>
        <img
          src="/Login.avif"
          alt="Meeting Invitation"
          className="w-full h-full object-cover object-center absolute inset-0"
        />

        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 via-purple-900/70 to-pink-900/80" />

        <div className="relative z-10 flex flex-col items-center justify-center w-full h-full px-12">
          <div className="text-center">
            <img src="/Vela-Icon.png" alt="Vela Logo" className="w-80 h-80 mx-auto drop-shadow-2xl" />
          </div>
        </div>
      </div>

      {/* Right: Invitation Details (40%) */}
      <div className={`flex flex-col justify-center items-center w-full lg:w-[40%] h-screen px-8 py-8 relative overflow-y-auto ${darkMode ? 'bg-[#232336]' : 'bg-white'}`}>
        <div className="w-full max-w-md mx-auto my-auto">
          {/* Theme Toggle */}
          <div className="absolute top-4 right-4">
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`p-3 rounded-xl transition-all duration-200 ${darkMode ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-100 hover:bg-gray-200'}`}
            >
              {darkMode ? (
                <Sun className="w-5 h-5 text-white" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600" />
              )}
            </button>
          </div>

          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-6">
            <div className="relative inline-block">
              <img src="/Vela-Icon.png" alt="Vela Logo" className="w-16 h-16 mx-auto mb-3" />
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-pink-500 to-red-500 rounded-full flex items-center justify-center">
                <Sparkles className="w-2.5 h-2.5 text-white" />
              </div>
            </div>
          </div>

          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-2xl bg-gradient-to-br from-pink-600 to-red-500 shadow-lg">
              <Calendar className="w-7 h-7 text-white" />
            </div>
            <h2 className={`text-2xl font-bold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              You're Invited!
            </h2>
            <p className={`text-sm ${darkMode ? 'text-white/70' : 'text-gray-600'}`}>
              You've been invited to join a meeting
            </p>
          </div>

          {/* Status Banner */}
          {renderStatusBanner()}

          {/* Meeting Details Card */}
          {meeting && (
            <div className={`rounded-2xl p-5 mb-5 border ${darkMode ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}>
              <h3 className={`text-lg font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {meeting.title}
              </h3>

              {meeting.description && (
                <p className={`mb-3 text-sm ${darkMode ? 'text-white/70' : 'text-gray-600'}`}>
                  {meeting.description}
                </p>
              )}

              <div className="space-y-2.5">
                {/* Date */}
                <div className={`flex items-center gap-3 p-2.5 rounded-xl ${darkMode ? 'bg-white/5' : 'bg-white'}`}>
                  <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-pink-500 to-red-500 flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className={`text-xs font-medium ${darkMode ? 'text-white/50' : 'text-gray-500'}`}>Date</p>
                    <p className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {new Date(meeting.meeting_date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>

                {/* Time */}
                <div className={`flex items-center gap-3 p-2.5 rounded-xl ${darkMode ? 'bg-white/5' : 'bg-white'}`}>
                  <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-pink-500 to-red-500 flex items-center justify-center">
                    <Clock className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className={`text-xs font-medium ${darkMode ? 'text-white/50' : 'text-gray-500'}`}>Time</p>
                    <p className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {formatTimeTo12Hour(meeting.start_time)} - {formatTimeTo12Hour(meeting.end_time)}
                    </p>
                  </div>
                </div>

                {/* Host */}
                <div className={`flex items-center gap-3 p-2.5 rounded-xl ${darkMode ? 'bg-white/5' : 'bg-white'}`}>
                  <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-pink-500 to-red-500 flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className={`text-xs font-medium ${darkMode ? 'text-white/50' : 'text-gray-500'}`}>Hosted by</p>
                    <p className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {meeting?.created_by?.first_name || 'N/A'} {meeting?.created_by?.last_name || ''}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {renderActionButtons()}

          {/* Additional Info */}
          {!user && !error && meetingStatus !== 'ended' && meetingStatus !== 'upcoming' && (
            <div className={`mt-4 text-center p-3 rounded-xl ${darkMode ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-200'}`}>
              <p className={`text-xs ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                You'll need to sign in to accept this invitation
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite;